// Opt-in, anonymous telemetry.
//
// PRINCIPLES:
//   1. Off by default. Nothing is ever sent unless the user has explicitly
//      enabled "help improve stillwater" in the About overlay.
//   2. No account, no login, no IP linking, no device fingerprint.
//   3. The only identifier is a rotating anonymous session ID stored in
//      localStorage, which the user can wipe by clearing site data.
//   4. Only sound-preset choices are sent. No text, no timestamps beyond
//      the server-side received time, no reading of quotes, no breathing
//      or grounding session data.

const TELEMETRY_STORAGE_KEY = "stillwater.telemetry";
const SESSION_ID_KEY = "stillwater.telemetrySessionId";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const loadTelemetryPreference = () => {
  try {
    return localStorage.getItem(TELEMETRY_STORAGE_KEY) === "true";
  } catch (_) {
    return false;
  }
};

export const saveTelemetryPreference = (on) => {
  try {
    localStorage.setItem(TELEMETRY_STORAGE_KEY, String(on));
  } catch (_) { /* silent */ }
};

// Small helper that returns (and lazily creates) an anonymous session id.
// If the user turns telemetry off and back on again, we mint a fresh id so
// past events can't be linked to the new session.
export const getOrCreateSessionId = () => {
  try {
    const existing = localStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const id = `anon-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_ID_KEY, id);
    return id;
  } catch (_) {
    return "anon-transient";
  }
};

export const rotateSessionId = () => {
  try {
    localStorage.removeItem(SESSION_ID_KEY);
  } catch (_) { /* silent */ }
};

// Fire-and-forget. Silently drops if telemetry is off or the request fails.
export const logSoundEvent = async ({
  eventType,
  primaryId,
  secondaryId,
  blendId = null,
}) => {
  if (!loadTelemetryPreference()) return;
  try {
    await fetch(`${API}/sound-events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: getOrCreateSessionId(),
        event_type: eventType,
        primary_id: primaryId,
        secondary_id: secondaryId,
        blend_id: blendId,
      }),
      // keepalive so a page unload doesn't lose the last event.
      keepalive: true,
    });
  } catch (_) {
    /* silent — never let telemetry disrupt the calm */
  }
};
